import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import { useTenant } from '@/contexts/TenantContext';
import { API_URL } from '@/utils/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { toast } from 'sonner';
import {
  Calendar, ChevronLeft, ChevronRight, Users, Clock, Copy,
  Check, X, AlertCircle, RefreshCw, UserCheck, UserX, AlertTriangle,
  Moon, Filter, MapPin
} from 'lucide-react';

const API = API_URL;
const DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
const DAY_LABELS = {
  fr: { monday: 'Lun', tuesday: 'Mar', wednesday: 'Mer', thursday: 'Jeu', friday: 'Ven', saturday: 'Sam', sunday: 'Dim' },
  en: { monday: 'Mon', tuesday: 'Tue', wednesday: 'Wed', thursday: 'Thu', friday: 'Fri', saturday: 'Sat', sunday: 'Sun' }
};

function getWeekStart(date) {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  return new Date(d.setDate(diff)).toISOString().split('T')[0];
}

function addWeeks(dateStr, weeks) {
  const d = new Date(dateStr);
  d.setDate(d.getDate() + (weeks * 7));
  return d.toISOString().split('T')[0];
}

function formatWeekRange(weekStart, lang) {
  const start = new Date(weekStart);
  const end = new Date(weekStart);
  end.setDate(end.getDate() + 6);
  const options = { day: 'numeric', month: 'short' };
  return `${start.toLocaleDateString(lang === 'fr' ? 'fr-FR' : 'en-US', options)} - ${end.toLocaleDateString(lang === 'fr' ? 'fr-FR' : 'en-US', options)}`;
}

export default function SchedulingPage() {
  const { language } = useLanguage();
  const { user } = useAuth();
  const { effectiveTenantId, loading: tenantLoading } = useTenant();
  const fr = language === 'fr';

  const [weekStart, setWeekStart] = useState(getWeekStart(new Date()));
  const [employees, setEmployees] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [zones, setZones] = useState([]);
  const [schedules, setSchedules] = useState({});
  const [settings, setSettings] = useState({ defaultWorkDays: DAYS.slice(0, 5), defaultWeeklyHours: 40 });
  const [loading, setLoading] = useState(true);

  // Filters
  const [filterZone, setFilterZone] = useState('all');
  const [filterShift, setFilterShift] = useState('all');

  // Selection
  const [selectedEmployees, setSelectedEmployees] = useState([]);

  // Dialogs
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const [bulkDays, setBulkDays] = useState([]);
  const [bulkShiftId, setBulkShiftId] = useState('');
  const [copyDialogOpen, setCopyDialogOpen] = useState(false);
  const [targetWeeks, setTargetWeeks] = useState([]);

  const isSupervisor = user?.role === 'tenant_supervisor';

  /* ========== FETCH ========== */
  const fetchData = useCallback(async () => {
    if (!effectiveTenantId) return;
    try {
      setLoading(true);
      const empUrl = isSupervisor && user?.shiftId
        ? `${API}/employees?tenantId=${effectiveTenantId}&shiftId=${user.shiftId}`
        : `${API}/employees?tenantId=${effectiveTenantId}`;

      const [empRes, schedRes, settingsRes, shiftsRes, zonesRes] = await Promise.all([
        axios.get(empUrl),
        axios.get(`${API}/schedules?tenantId=${effectiveTenantId}&weekStart=${weekStart}`),
        axios.get(`${API}/settings/${effectiveTenantId}`),
        axios.get(`${API}/shifts?tenantId=${effectiveTenantId}`),
        axios.get(`${API}/zones?tenantId=${effectiveTenantId}`)
      ]);

      setEmployees(empRes.data.filter(e => e.active));
      setShifts(shiftsRes.data);
      setZones(zonesRes.data);

      const schedMap = {};
      schedRes.data.forEach(s => { schedMap[s.employeeId] = s; });
      setSchedules(schedMap);
      setSettings(settingsRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error(fr ? 'Erreur de chargement' : 'Loading error');
    } finally {
      setLoading(false);
    }
  }, [effectiveTenantId, weekStart, fr, isSupervisor, user?.shiftId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  /* ========== FILTERED EMPLOYEES ========== */
  const filteredEmployees = useMemo(() => {
    let result = [...employees];
    if (filterZone !== 'all') result = result.filter(e => e.zoneId === filterZone);
    if (filterShift !== 'all') {
      if (filterShift === 'none') result = result.filter(e => !e.shiftId);
      else result = result.filter(e => e.shiftId === filterShift);
    }
    return result;
  }, [employees, filterZone, filterShift]);

  /* ========== HELPERS ========== */
  const getEmployeeDays = (employeeId) => {
    const schedule = schedules[employeeId];
    return schedule ? schedule.scheduledDays || [] : settings.defaultWorkDays || DAYS.slice(0, 5);
  };

  const getDayShift = (employeeId, day) => {
    const schedule = schedules[employeeId];
    if (schedule?.dayShifts?.[day]) return schedule.dayShifts[day];
    // fallback to employee's assigned shift
    const emp = employees.find(e => e.id === employeeId);
    return emp?.shiftId || null;
  };

  const isDefaultSchedule = (employeeId) => !schedules[employeeId];

  const getShiftName = (shiftId) => shifts.find(s => s.id === shiftId)?.name || null;
  const getShiftShort = (shiftId) => {
    const s = shifts.find(sh => sh.id === shiftId);
    return s ? `${s.startTime?.slice(0,5) || ''}` : null;
  };
  const getZoneName = (zoneId) => zones.find(z => z.id === zoneId)?.name || '-';

  /* ========== KPIs ========== */
  const kpis = useMemo(() => {
    const defaultDays = settings.defaultWorkDays || DAYS.slice(0, 5);
    let planned = 0, unplanned = 0;
    filteredEmployees.forEach(emp => {
      const schedule = schedules[emp.id];
      if (schedule && schedule.scheduledDays?.length > 0) {
        planned++;
      } else if (!schedule && defaultDays.length > 0) {
        planned++; // default schedule
      } else {
        unplanned++;
      }
    });
    return { total: filteredEmployees.length, planned, unplanned };
  }, [filteredEmployees, schedules, settings.defaultWorkDays]);

  /* ========== CELL ACTIONS ========== */
  const handleCellAction = async (employeeId, day, shiftId) => {
    const schedule = schedules[employeeId];
    const currentDays = schedule ? [...(schedule.scheduledDays || [])] : [...(settings.defaultWorkDays || DAYS.slice(0, 5))];
    const currentDayShifts = schedule?.dayShifts ? { ...schedule.dayShifts } : {};

    let newDays;
    if (shiftId === 'off') {
      // Set to off
      newDays = currentDays.filter(d => d !== day);
      delete currentDayShifts[day];
    } else {
      // Schedule with shift
      if (!currentDays.includes(day)) newDays = [...currentDays, day];
      else newDays = currentDays;
      if (shiftId) currentDayShifts[day] = shiftId;
    }

    try {
      await axios.post(`${API}/schedules?tenantId=${effectiveTenantId}`, {
        employeeId, weekStart, scheduledDays: newDays, dayShifts: currentDayShifts
      });
      setSchedules(prev => ({
        ...prev,
        [employeeId]: { ...prev[employeeId], scheduledDays: newDays, dayShifts: currentDayShifts, employeeId, weekStart }
      }));
    } catch (error) {
      toast.error(fr ? 'Erreur de mise à jour' : 'Update error');
    }
  };

  /* ========== BULK ========== */
  const handleBulkSchedule = async () => {
    if (selectedEmployees.length === 0 || bulkDays.length === 0) return;
    const dayShifts = {};
    if (bulkShiftId) bulkDays.forEach(d => { dayShifts[d] = bulkShiftId; });
    try {
      await axios.post(`${API}/schedules/bulk?tenantId=${effectiveTenantId}`, {
        employeeIds: selectedEmployees, weekStart, scheduledDays: bulkDays, dayShifts
      });
      toast.success(fr ? `${selectedEmployees.length} employés planifiés` : `${selectedEmployees.length} employees scheduled`);
      setBulkDialogOpen(false); setSelectedEmployees([]); setBulkDays([]); setBulkShiftId(''); fetchData();
    } catch (error) {
      toast.error(fr ? 'Erreur' : 'Error');
    }
  };

  /* ========== COPY ========== */
  const handleCopyWeek = async () => {
    if (targetWeeks.length === 0) return;
    try {
      const res = await axios.post(
        `${API}/schedules/copy-week?tenantId=${effectiveTenantId}&fromWeek=${weekStart}&toWeeks=${targetWeeks.join(',')}`
      );
      toast.success(fr ? `${res.data.copied} planifications copiées vers ${targetWeeks.length} semaine(s)` : `${res.data.copied} schedules copied to ${targetWeeks.length} week(s)`);
      setCopyDialogOpen(false); setTargetWeeks([]);
    } catch (error) {
      toast.error(fr ? 'Erreur' : 'Error');
    }
  };

  /* ========== SELECT ========== */
  const toggleSelectAll = () => {
    setSelectedEmployees(selectedEmployees.length === filteredEmployees.length ? [] : filteredEmployees.map(e => e.id));
  };

  const toggleTarget = (week) => {
    setTargetWeeks(prev => prev.includes(week) ? prev.filter(w => w !== week) : [...prev, week]);
  };

  if (loading || tenantLoading) {
    return <div className="flex items-center justify-center h-64"><RefreshCw className="animate-spin text-primary" size={32} /></div>;
  }

  return (
    <div className="space-y-4" data-testid="scheduling-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900 dark:text-white">{fr ? 'Planification' : 'Scheduling'}</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400">{fr ? 'Organisation des quarts et horaires' : 'Shift and schedule management'}</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" className="gap-1.5 h-8" onClick={() => setCopyDialogOpen(true)} data-testid="copy-week-btn">
            <Copy size={14} /> {fr ? 'Copier semaine' : 'Copy Week'}
          </Button>
          {selectedEmployees.length > 0 && (
            <Button size="sm" className="gap-1.5 h-8" onClick={() => { setBulkDays(settings.defaultWorkDays || DAYS.slice(0, 5)); setBulkDialogOpen(true); }} data-testid="bulk-schedule-btn">
              <Users size={14} /> {fr ? `Planifier ${selectedEmployees.length}` : `Schedule ${selectedEmployees.length}`}
            </Button>
          )}
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-3 gap-3" data-testid="scheduling-kpis">
        <div className="flex items-center gap-3 p-3 rounded-lg border dark:border-slate-700 bg-blue-50/50 dark:bg-blue-950/20">
          <Users size={18} className="text-blue-600 dark:text-blue-400" />
          <div><p className="text-lg font-bold text-slate-800 dark:text-white">{kpis.total}</p><p className="text-[10px] text-slate-500 dark:text-slate-400 uppercase tracking-wide">{fr ? 'Total employés' : 'Total employees'}</p></div>
        </div>
        <div className="flex items-center gap-3 p-3 rounded-lg border dark:border-slate-700 bg-green-50/50 dark:bg-green-950/20">
          <UserCheck size={18} className="text-green-600 dark:text-green-400" />
          <div><p className="text-lg font-bold text-slate-800 dark:text-white">{kpis.planned}</p><p className="text-[10px] text-slate-500 dark:text-slate-400 uppercase tracking-wide">{fr ? 'Planifiés' : 'Planned'}</p></div>
        </div>
        <div className="flex items-center gap-3 p-3 rounded-lg border dark:border-slate-700 bg-amber-50/50 dark:bg-amber-950/20">
          <UserX size={18} className="text-amber-600 dark:text-amber-400" />
          <div><p className="text-lg font-bold text-slate-800 dark:text-white">{kpis.unplanned}</p><p className="text-[10px] text-slate-500 dark:text-slate-400 uppercase tracking-wide">{fr ? 'Non planifiés' : 'Unplanned'}</p></div>
        </div>
      </div>

      {/* Week Nav + Filters */}
      <Card className="dark:border-slate-700">
        <CardContent className="p-3">
          <div className="flex items-center justify-between">
            <Button variant="ghost" size="sm" onClick={() => setWeekStart(addWeeks(weekStart, -1))} data-testid="prev-week-btn"><ChevronLeft size={18} /></Button>
            <div className="text-center">
              <div className="flex items-center gap-2 justify-center font-semibold text-slate-800 dark:text-white">
                <Calendar size={16} className="text-primary" />
                <span className="text-sm">{fr ? 'Semaine du' : 'Week of'} {formatWeekRange(weekStart, language)}</span>
              </div>
            </div>
            <Button variant="ghost" size="sm" onClick={() => setWeekStart(addWeeks(weekStart, 1))} data-testid="next-week-btn"><ChevronRight size={18} /></Button>
          </div>
          <div className="flex items-center gap-2 mt-2 pt-2 border-t dark:border-slate-700">
            <Filter size={12} className="text-slate-400" />
            <Select value={filterZone} onValueChange={setFilterZone}>
              <SelectTrigger className="h-7 w-36 text-xs" data-testid="filter-zone"><MapPin size={10} className="mr-1" /><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{fr ? 'Toutes zones' : 'All zones'}</SelectItem>
                {zones.map(z => <SelectItem key={z.id} value={z.id}>{z.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={filterShift} onValueChange={setFilterShift}>
              <SelectTrigger className="h-7 w-36 text-xs" data-testid="filter-shift"><Clock size={10} className="mr-1" /><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{fr ? 'Tous quarts' : 'All shifts'}</SelectItem>
                <SelectItem value="none">{fr ? 'Non assigné' : 'Unassigned'}</SelectItem>
                {shifts.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Schedule Grid */}
      <Card className="dark:border-slate-700" data-testid="schedule-grid">
        <CardHeader className="py-2 px-4 border-b dark:border-slate-700">
          <CardTitle className="text-sm flex items-center justify-between">
            <span className="text-slate-700 dark:text-slate-300">{fr ? 'Grille de planification' : 'Schedule Grid'}</span>
            <div className="flex items-center gap-3 text-[10px] font-normal text-slate-500 dark:text-slate-400">
              <span className="flex items-center gap-1"><div className="w-3 h-3 rounded bg-green-500/20 border border-green-500/40"></div>{fr ? 'Planifié' : 'Scheduled'}</span>
              <span className="flex items-center gap-1"><div className="w-3 h-3 rounded bg-slate-200 dark:bg-slate-700 border border-slate-300 dark:border-slate-600"></div>{fr ? 'Repos' : 'Off'}</span>
              <span className="flex items-center gap-1"><Badge variant="outline" className="text-[9px] px-1 py-0">Def</Badge>{fr ? 'Par défaut' : 'Default'}</span>
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-slate-50/80 dark:bg-slate-800/80 border-b dark:border-slate-700">
                  <th className="p-2 text-left w-8">
                    <Checkbox checked={selectedEmployees.length === filteredEmployees.length && filteredEmployees.length > 0} onCheckedChange={toggleSelectAll} data-testid="select-all-checkbox" />
                  </th>
                  <th className="p-2 text-left text-xs font-bold text-slate-700 dark:text-slate-300 w-44">{fr ? 'Employé' : 'Employee'}</th>
                  {DAYS.map(day => (
                    <th key={day} className={`p-2 text-center text-xs font-bold w-28 ${settings.defaultWorkDays?.includes(day) ? 'text-slate-800 dark:text-slate-200' : 'text-slate-400 dark:text-slate-500'}`}>
                      {DAY_LABELS[language][day]}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredEmployees.length === 0 ? (
                  <tr><td colSpan={9} className="text-center py-12 text-slate-400">{fr ? 'Aucun employé trouvé' : 'No employees found'}</td></tr>
                ) : (
                  filteredEmployees.map((emp, idx) => {
                    const empDays = getEmployeeDays(emp.id);
                    const isDefault = isDefaultSchedule(emp.id);
                    return (
                      <tr key={emp.id} className={`border-b dark:border-slate-800 ${idx % 2 === 1 ? 'bg-slate-25 dark:bg-slate-900/30' : ''} ${selectedEmployees.includes(emp.id) ? 'bg-primary/5 dark:bg-primary/10' : ''}`} data-testid={`schedule-row-${emp.employeeCode}`}>
                        <td className="p-2">
                          <Checkbox checked={selectedEmployees.includes(emp.id)} onCheckedChange={(checked) => { setSelectedEmployees(prev => checked ? [...prev, emp.id] : prev.filter(id => id !== emp.id)); }} />
                        </td>
                        <td className="p-2">
                          <div className="flex items-center gap-1.5">
                            <div>
                              <p className="font-medium text-sm text-slate-900 dark:text-slate-100 leading-tight">{emp.firstName} {emp.lastName}</p>
                              <p className="text-[10px] text-slate-500 dark:text-slate-400">{emp.employeeCode} {isDefault && <Badge variant="outline" className="text-[8px] px-1 py-0 ml-1">Def</Badge>}</p>
                            </div>
                          </div>
                        </td>
                        {DAYS.map(day => {
                          const isScheduled = empDays.includes(day);
                          const dayShiftId = getDayShift(emp.id, day);
                          const shiftName = dayShiftId ? getShiftName(dayShiftId) : null;
                          const shiftTime = dayShiftId ? getShiftShort(dayShiftId) : null;

                          return (
                            <td key={day} className="p-1 text-center">
                              <Popover>
                                <PopoverTrigger asChild>
                                  <button
                                    className={`w-full h-12 rounded-lg border transition-all text-xs flex flex-col items-center justify-center gap-0.5 ${
                                      isScheduled
                                        ? 'bg-green-50 dark:bg-green-950/30 border-green-300 dark:border-green-800 hover:bg-green-100 dark:hover:bg-green-950/50'
                                        : 'bg-slate-50 dark:bg-slate-800/40 border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-700/40'
                                    }`}
                                    data-testid={`cell-${emp.employeeCode}-${day}`}
                                  >
                                    {isScheduled ? (
                                      <>
                                        <Check size={12} className="text-green-600 dark:text-green-400" />
                                        {shiftName && <span className="text-[9px] font-medium text-green-700 dark:text-green-400 truncate max-w-[60px]">{shiftName}</span>}
                                        {shiftTime && <span className="text-[8px] text-green-600/70 dark:text-green-500/70">{shiftTime}</span>}
                                      </>
                                    ) : (
                                      <Moon size={10} className="text-slate-300 dark:text-slate-600" />
                                    )}
                                  </button>
                                </PopoverTrigger>
                                <PopoverContent className="w-44 p-1" align="center">
                                  <div className="space-y-0.5">
                                    <p className="text-[10px] font-semibold text-slate-500 px-2 py-1">{fr ? 'Choisir quart' : 'Choose shift'}</p>
                                    {shifts.map(s => (
                                      <button
                                        key={s.id}
                                        onClick={() => handleCellAction(emp.id, day, s.id)}
                                        className={`w-full text-left px-2 py-1.5 rounded text-xs hover:bg-green-50 dark:hover:bg-green-950/30 flex items-center justify-between ${dayShiftId === s.id ? 'bg-green-50 dark:bg-green-950/30 font-semibold text-green-700 dark:text-green-400' : 'text-slate-700 dark:text-slate-300'}`}
                                        data-testid={`assign-${emp.employeeCode}-${day}-${s.id}`}
                                      >
                                        <span>{s.name}</span>
                                        <span className="text-[10px] text-slate-400">{s.startTime}-{s.endTime}</span>
                                      </button>
                                    ))}
                                    <hr className="dark:border-slate-700" />
                                    <button
                                      onClick={() => handleCellAction(emp.id, day, 'off')}
                                      className="w-full text-left px-2 py-1.5 rounded text-xs hover:bg-red-50 dark:hover:bg-red-950/30 text-red-600 dark:text-red-400 flex items-center gap-1"
                                      data-testid={`off-${emp.employeeCode}-${day}`}
                                    >
                                      <Moon size={10} /> {fr ? 'Repos' : 'Day Off'}
                                    </button>
                                  </div>
                                </PopoverContent>
                              </Popover>
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Info Banner */}
      <div className="bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3 flex items-start gap-2 text-sm">
        <AlertCircle className="text-blue-500 dark:text-blue-400 mt-0.5 shrink-0" size={16} />
        <div className="text-blue-700 dark:text-blue-400">
          <p className="font-medium text-xs">{fr ? 'Indicateurs de cohérence' : 'Coherence indicators'}</p>
          <p className="text-xs mt-0.5 text-blue-600 dark:text-blue-500">
            {fr ? 'Les rapports détectent automatiquement les incohérences: "présent non planifié" et "planifié absent".' : 'Reports automatically detect inconsistencies: "present but not scheduled" and "scheduled but absent".'}
          </p>
        </div>
      </div>

      {/* Bulk Schedule Dialog */}
      <Dialog open={bulkDialogOpen} onOpenChange={setBulkDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users size={18} /> {fr ? `Planifier ${selectedEmployees.length} employé(s)` : `Schedule ${selectedEmployees.length} employee(s)`}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <p className="text-xs font-semibold text-slate-500 mb-2">{fr ? 'Quart à assigner:' : 'Shift to assign:'}</p>
              <Select value={bulkShiftId || 'default'} onValueChange={v => setBulkShiftId(v === 'default' ? '' : v)}>
                <SelectTrigger className="h-8 text-sm" data-testid="bulk-shift-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="default">{fr ? 'Quart par défaut de l\'employé' : 'Employee default shift'}</SelectItem>
                  {shifts.map(s => <SelectItem key={s.id} value={s.id}>{s.name} ({s.startTime}-{s.endTime})</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <p className="text-xs font-semibold text-slate-500 mb-2">{fr ? 'Jours de travail:' : 'Work days:'}</p>
              <div className="grid grid-cols-4 gap-1.5">
                {DAYS.map(day => (
                  <button key={day} onClick={() => setBulkDays(prev => prev.includes(day) ? prev.filter(d => d !== day) : [...prev, day])}
                    className={`p-2 rounded-lg border text-xs font-medium transition-all ${bulkDays.includes(day) ? 'bg-primary text-white border-primary' : 'bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 hover:border-primary text-slate-700 dark:text-slate-300'}`}>
                    {DAY_LABELS[language][day]}
                  </button>
                ))}
              </div>
              <div className="flex gap-1.5 mt-2">
                <Button variant="outline" size="sm" className="text-xs h-7" onClick={() => setBulkDays(settings.defaultWorkDays || DAYS.slice(0, 5))}>{fr ? 'Par défaut' : 'Default'}</Button>
                <Button variant="outline" size="sm" className="text-xs h-7" onClick={() => setBulkDays(DAYS)}>{fr ? 'Tous' : 'All'}</Button>
                <Button variant="outline" size="sm" className="text-xs h-7" onClick={() => setBulkDays([])}>{fr ? 'Aucun' : 'None'}</Button>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkDialogOpen(false)}>{fr ? 'Annuler' : 'Cancel'}</Button>
            <Button onClick={handleBulkSchedule} disabled={bulkDays.length === 0} data-testid="confirm-bulk-schedule-btn">{fr ? 'Appliquer' : 'Apply'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Copy Week Dialog */}
      <Dialog open={copyDialogOpen} onOpenChange={setCopyDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Copy size={18} /> {fr ? 'Copier la semaine' : 'Copy Week'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm text-slate-600 dark:text-slate-400">
              {fr ? `Copier depuis: ${formatWeekRange(weekStart, language)}` : `Copy from: ${formatWeekRange(weekStart, language)}`}
            </p>
            <p className="text-xs font-semibold text-slate-500">{fr ? 'Vers (sélection multiple):' : 'To (multi-select):'}</p>
            <div className="grid grid-cols-2 gap-1.5">
              {[1, 2, 3, 4, 5, 6].map(i => {
                const nextWeek = addWeeks(weekStart, i);
                const selected = targetWeeks.includes(nextWeek);
                return (
                  <button key={i} onClick={() => toggleTarget(nextWeek)}
                    className={`p-2.5 rounded-lg border text-xs transition-all ${selected ? 'bg-primary text-white border-primary' : 'bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 hover:border-primary text-slate-700 dark:text-slate-300'}`}
                    data-testid={`copy-target-${i}`}>
                    {selected && <Check size={10} className="inline mr-1" />}
                    {formatWeekRange(nextWeek, language)}
                  </button>
                );
              })}
            </div>
            {targetWeeks.length > 0 && (
              <p className="text-xs text-primary font-medium">{targetWeeks.length} {fr ? 'semaine(s) sélectionnée(s)' : 'week(s) selected'}</p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCopyDialogOpen(false)}>{fr ? 'Annuler' : 'Cancel'}</Button>
            <Button onClick={handleCopyWeek} disabled={targetWeeks.length === 0} data-testid="confirm-copy-btn">
              <Copy size={14} className="mr-1" /> {fr ? 'Copier' : 'Copy'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
